import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { ConvexReactClient } from 'convex/react';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ConvexErrorBoundary } from './components/errors/ConvexErrorBoundary';
import { ClerkErrorBoundary } from './components/errors/ClerkErrorBoundary';
import { error as logError, warn as logWarn } from './lib/logger';

// Brand typefaces, self-hosted. Loaded from npm rather than the Google Fonts
// CDN so the strict CSP in amplify.yml does not need a remote font origin.
// EB Garamond = display (Brand DNA Guide, Layer 5); Inter = body and UI.
import '@fontsource/eb-garamond/400.css';
import '@fontsource/eb-garamond/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import './index.css';

import { startTracking } from './lib/trackingBootstrap';

// Start analytics before first render so the initial pageview is captured.
// No-ops without consent, and without the relevant env vars. Attribution
// capture inside runs regardless — see trackingBootstrap.ts for why.
startTracking();

// Initialize Convex client with error handling
const convexUrl = import.meta.env.VITE_CONVEX_URL;

function createConvexClient(): ConvexReactClient | null {
  if (!convexUrl) return null;

  try {
    return new ConvexReactClient(convexUrl);
  } catch (error) {
    logError('Failed to create Convex client:', error instanceof Error ? error : new Error(String(error)));
    return null;
  }
}

// Get Clerk publishable key (following official Clerk docs)
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// For development, we'll allow the app to run even without keys
// This lets you see the UI while setting up the services
if (!PUBLISHABLE_KEY) {
  logWarn('Missing VITE_CLERK_PUBLISHABLE_KEY in .env.local - see docs/LOCAL_DEV_SETUP_GUIDE.md');
}

if (!convexUrl) {
  logWarn('Missing VITE_CONVEX_URL in .env.local - see docs/LOCAL_DEV_SETUP_GUIDE.md');
}

// Conditional rendering based on available services
const AppWithProviders = () => {
  const [convexClient, setConvexClient] = useState<ConvexReactClient | null>(() => createConvexClient());

  const handleConvexRetry = () => {
    const newClient = createConvexClient();
    setConvexClient(newClient);
  };

  // If both services are configured, use them
  if (PUBLISHABLE_KEY && convexClient) {
    return (
      <React.StrictMode>
        <ClerkErrorBoundary>
          <ConvexErrorBoundary onRetry={handleConvexRetry}>
            <ClerkProvider
              publishableKey={PUBLISHABLE_KEY}
              afterSignOutUrl="/"
            >
              <ConvexProviderWithClerk client={convexClient} useAuth={useAuth}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </ConvexProviderWithClerk>
            </ClerkProvider>
          </ConvexErrorBoundary>
        </ClerkErrorBoundary>
      </React.StrictMode>
    );
  }

  // Otherwise, just render the app with Router only
  // This allows development to continue while setting up services
  return (
    <React.StrictMode>
      <BrowserRouter>
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          padding: '10px',
          margin: '10px',
          borderRadius: '4px'
        }}>
          <strong>⚠️ Development Mode:</strong> Clerk and Convex not configured.
          See <code>docs/LOCAL_DEV_SETUP_GUIDE.md</code> for setup instructions.
        </div>
        <App />
      </BrowserRouter>
    </React.StrictMode>
  );
};

createRoot(document.getElementById('root')!).render(<AppWithProviders />);
